// F10 配車リアルタイム配信のクライアント側状態。
//
// メッセージ形 (snapshot / delta) の単一源泉はサーバ apps/api/src/realtime/fleet-state.ts。
// admin はそれを購読し、地図マーカー・進捗・在席のオーバーレイに使う。
// ここはランタイム非依存の純粋リデューサ (WebSocket 管理は use-fleet-stream.ts)。

export type PresenceStatus = "on_duty" | "on_break" | "off_duty";

export interface DriverLocation {
  lat: number;
  lng: number;
  heading?: number;
  speedKmh?: number;
  accuracyM?: number;
  recordedAt: number;
}

export interface DriverProgress {
  planPublicId: string;
  completed: number;
  total: number;
  currentStopPublicId?: string;
  at: number;
}

export interface DriverLiveState {
  driverId: string;
  presence?: PresenceStatus;
  location?: DriverLocation;
  progress?: DriverProgress;
  updatedAt: number;
}

interface LocationEvent {
  type: "location";
  driverId: string;
  lat: number;
  lng: number;
  heading?: number;
  speedKmh?: number;
  accuracyM?: number;
  recordedAt: number;
}
interface PresenceEvent {
  type: "presence";
  driverId: string;
  status: PresenceStatus;
  at: number;
}
interface ProgressEvent {
  type: "progress";
  driverId: string;
  planPublicId: string;
  completed: number;
  total: number;
  currentStopPublicId?: string;
  at: number;
}
export type FleetEvent = LocationEvent | PresenceEvent | ProgressEvent;

export type FleetMessage =
  | { type: "snapshot"; drivers: DriverLiveState[]; at: number }
  | { type: "delta"; event: FleetEvent };

export type FleetState = ReadonlyMap<string, DriverLiveState>;
export type QueryKeyPrefix = readonly unknown[];

export function emptyFleetState(): FleetState {
  return new Map();
}

function reduceDriver(prev: DriverLiveState | undefined, event: FleetEvent): DriverLiveState {
  const base: DriverLiveState = prev ?? { driverId: event.driverId, updatedAt: 0 };
  switch (event.type) {
    case "location":
      return {
        ...base,
        updatedAt: event.recordedAt,
        location: {
          lat: event.lat,
          lng: event.lng,
          heading: event.heading,
          speedKmh: event.speedKmh,
          accuracyM: event.accuracyM,
          recordedAt: event.recordedAt,
        },
      };
    case "presence":
      return { ...base, updatedAt: event.at, presence: event.status };
    case "progress":
      return {
        ...base,
        updatedAt: event.at,
        progress: {
          planPublicId: event.planPublicId,
          completed: event.completed,
          total: event.total,
          currentStopPublicId: event.currentStopPublicId,
          at: event.at,
        },
      };
  }
}

/** snapshot は全置換、delta は単一ドライバー更新。常に新しい Map を返す。 */
export function applyFleetMessage(state: FleetState, msg: FleetMessage): FleetState {
  if (msg.type === "snapshot") {
    return new Map(msg.drivers.map((d) => [d.driverId, d]));
  }
  const copy = new Map(state);
  copy.set(msg.event.driverId, reduceDriver(copy.get(msg.event.driverId), msg.event));
  return copy;
}

/** 文字列が FleetMessage か最小判定する (WebSocket 受信のガード)。 */
export function parseFleetMessage(raw: string): FleetMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const m = value as { type?: unknown };
  if (m.type === "snapshot" || m.type === "delta") return value as FleetMessage;
  return null;
}

/** http(s) ベース URL を ws(s) の購読 URL に変換する。 */
export function fleetStreamUrl(apiBaseUrl: string): string {
  const ws = apiBaseUrl.replace(/^http/, "ws").replace(/\/$/, "");
  return `${ws}/api/v1/admin/fleet/stream`;
}

/**
 * FleetHub の push イベントで即時 stale 扱いにできる query key prefix。
 * 通知一覧そのものは FleetHub のイベント範囲外なので polling を残し、運行系だけを寄せる。
 */
export function fleetInvalidationKeys(msg: FleetMessage): QueryKeyPrefix[] {
  if (msg.type !== "delta") return [];
  if (msg.event.type === "location" || msg.event.type === "presence") {
    return [];
  }
  return [
    ["driver-status"],
    ["instruction-status"],
    ["dashboard-stats"],
    ["dashboard-alerts"],
    ["nav-badges", "stats"],
    ["nav-badges", "alerts"],
  ];
}
